import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Skeleton from '../../../componentsv2/Skeleton';
import Typography from '../../../componentsv2/Typography';
import Button from '../../../componentsv2/Button';
import Tooltip from '../../../componentsv2/Tooltip';
import Chip from '../../../componentsv2/Chip';
import StepButtons from './StepButtons';
import { useTranslation } from '../../../hooks/i18n';
import { COLORS } from '../../../themev2';
import { APIStakeholder } from '../../../types/documents';
import { AddIcon, ChevronDownIcon, ChevronRightIcon, EditIcon, TrashIcon } from '../../../components/Icons';
import Modal from '../../../componentsv2/Modal';
import { useEffect, useMemo, useState } from 'react';
import AddEditStakeholderDrawer from '../../CompanyUserDocuments/AddEditStakeholder';
import { getRequiredDocumentSlots, PERSON_TYPE } from '../../../constants/data';
import {
  addCompanyLegalRepresentative,
  addCompanyShareholder,
  deleteCompanyShareholder,
  editCompanyLegalRepresentative,
  editCompanyShareholder,
  getCompanyLegalRepresentatives,
  getCompanyShareholders,
  updateLegalRepresentativeAsset,
  updateShareholderAsset,
} from '../../../services/documents';

interface OwnershipProps {
  companyId: number;
  companyName: string;
  onNext: (metrics: { shareholderCount: number; controllingPersonCount: number }) => void;
  onBack: () => void;
  isNextLoading?: boolean;
}

const FULL_SHARE = 100;
// Shares are read back as floats, so 33.33 + 33.33 + 33.34 needs a tolerance.
const SHARE_TOTAL_TOLERANCE = 0.01;

const isJuridical = (stakeholder: APIStakeholder) => stakeholder.personType === PERSON_TYPE.JURIDICAL;

const Ownership = ({ companyId, companyName, onNext, onBack, isNextLoading }: OwnershipProps) => {
  const { t } = useTranslation(['activation', 'client']);
  const [shareholders, setShareholders] = useState<APIStakeholder[]>([]);
  const [controllingPersons, setControllingPersons] = useState<APIStakeholder[]>([]);
  const [isLoadingShareholders, setIsLoadingShareholders] = useState(false);
  const [isLoadingControllingPersons, setIsLoadingControllingPersons] = useState(false);
  const [collapsedBlocks, setCollapsedBlocks] = useState<number[]>([]);

  const [showShareholderDrawer, setShowShareholderDrawer] = useState(false);
  const [showControllingPersonDrawer, setShowControllingPersonDrawer] = useState(false);
  const [currentShareholder, setCurrentShareholder] = useState<APIStakeholder | null>(null);
  const [currentControllingPerson, setCurrentControllingPerson] = useState<APIStakeholder | null>(null);
  const [currentParent, setCurrentParent] = useState<APIStakeholder | null>(null);
  const [shareholderToDelete, setShareholderToDelete] = useState<APIStakeholder | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadShareholders = () => {
    setIsLoadingShareholders(true);
    return getCompanyShareholders(companyId, true)
      .then((data) => {
        setShareholders(data);
      })
      .finally(() => {
        setIsLoadingShareholders(false);
      });
  };

  const loadControllingPersons = () => {
    setIsLoadingControllingPersons(true);
    return getCompanyLegalRepresentatives(companyId)
      .then((data) => {
        setControllingPersons(data);
      })
      .finally(() => {
        setIsLoadingControllingPersons(false);
      });
  };

  useEffect(() => {
    if (companyId) {
      loadShareholders();
      loadControllingPersons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const closeShareholderDrawer = () => {
    setCurrentShareholder(null);
    setCurrentParent(null);
    setShowShareholderDrawer(false);
    loadShareholders();
  };

  const closeControllingPersonDrawer = () => {
    setCurrentControllingPerson(null);
    setShowControllingPersonDrawer(false);
    // Always reload both lists: a newly added controlling person might have been
    // saved as a shareholder (is_controlling_person=true), in which case it won't
    // appear in the legal_representatives endpoint.
    loadShareholders();
    loadControllingPersons();
  };

  // The ownership chain arrives flattened, each shareholder carrying its parent.
  const blocksByParentId = useMemo(() => {
    const blocks = new Map<number | null, APIStakeholder[]>();
    shareholders.forEach((shareholder) => {
      const block = blocks.get(shareholder.parentId) ?? [];
      block.push(shareholder);
      blocks.set(shareholder.parentId, block);
    });
    return blocks;
  }, [shareholders]);

  const rootShareholders = blocksByParentId.get(null) ?? [];

  // Shareholders marked as is_controlling_person are shown in both sections.
  const isBothShareholders = shareholders.filter((s) => s.isControllingPerson);
  const effectiveControllingPersons = [...controllingPersons, ...isBothShareholders];

  // Every juridical shareholder has to declare its own shareholders, so that the
  // chain always ends in the physical persons that ultimately own the company.
  const hasIncompleteOwnershipChain = shareholders.some(
    (shareholder) => isJuridical(shareholder) && !blocksByParentId.get(shareholder.id)?.length
  );

  const isNextDisabled =
    rootShareholders.length === 0 || effectiveControllingPersons.length === 0 || hasIncompleteOwnershipChain;

  const getMissingDocumentCount = (shareholder: APIStakeholder) => {
    const uploadedTypes = shareholder.assets.map((asset) => asset.assetType);
    if (!isJuridical(shareholder)) {
      return { missing: shareholder.assets.length === 0 ? 1 : 0, total: 1 };
    }
    const requiredSlots = getRequiredDocumentSlots(
      shareholder.countryOfIncorporation ?? '',
      shareholder.businessStructure ?? ''
    );
    return {
      missing: requiredSlots.filter((slotKey) => !uploadedTypes.includes(slotKey)).length,
      total: requiredSlots.length,
    };
  };

  const handleEditControllingPerson = (person: APIStakeholder) => {
    if (person.isControllingPerson) {
      // This is actually a shareholder stored with is_controlling_person=true.
      // Edit it through the shareholder drawer so it uses shareholder endpoints.
      setCurrentShareholder(person);
      setShowShareholderDrawer(true);
    } else {
      setCurrentControllingPerson(person);
      setShowControllingPersonDrawer(true);
    }
  };

  const openAddShareholder = (parent: APIStakeholder | null) => {
    setCurrentShareholder(null);
    setCurrentParent(parent);
    setShowShareholderDrawer(true);
  };

  const openEditShareholder = (shareholder: APIStakeholder) => {
    setCurrentShareholder(shareholder);
    setCurrentParent(shareholders.find(({ id }) => id === shareholder.parentId) ?? null);
    setShowShareholderDrawer(true);
  };

  const countDescendants = (shareholder: APIStakeholder): number => {
    const children = blocksByParentId.get(shareholder.id) ?? [];
    return children.reduce((total, child) => total + 1 + countDescendants(child), 0);
  };

  const handleDeleteShareholder = () => {
    setIsDeleting(true);
    deleteCompanyShareholder(companyId, shareholderToDelete!.id, false)
      .then(() => {
        setShareholderToDelete(null);
        loadShareholders();
      })
      .finally(() => setIsDeleting(false));
  };

  const renderStatusChip = (shareholder: APIStakeholder) => {
    const { missing, total } = getMissingDocumentCount(shareholder);
    if (missing === 0) {
      return (
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '21px',
            color: COLORS.feedback.success,
            backgroundColor: COLORS.feedback.successBackground,
            borderRadius: '10px',
            padding: '4px 10px',
          }}
        >
          {t('activation:step4.complete')}
        </Box>
      );
    }
    return (
      <Box
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '21px',
          color: COLORS.orange[900],
          backgroundColor: COLORS.orange[100],
          borderRadius: '10px',
          padding: '4px 10px',
        }}
      >
        {t('activation:step4.documentsProgress', { uploaded: total - missing, total })}
      </Box>
    );
  };

  const renderShareholderRow = (shareholder: APIStakeholder, ownerName: string) => (
    <Box
      key={shareholder.id}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        padding: '12px 0',
        borderTop: `1px solid ${COLORS.gray[300]}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography
            sx={{ fontSize: '16px', fontWeight: 600, color: COLORS.gray[1200], lineHeight: '24px' }}
          >
            {shareholder.fullName}
          </Typography>
          <Chip
            label={
              isJuridical(shareholder)
                ? t('client:documents.shareholders.juridical')
                : t('client:documents.shareholders.physical')
            }
            size="small"
            sx={{
              backgroundColor: COLORS.gray[400],
              color: COLORS.gray[1200],
              borderRadius: '4px',
              padding: '2px 6px',
              height: 'auto',
            }}
          />
        </Box>
        <Typography sx={{ fontSize: '16px', color: COLORS.gray[1100], lineHeight: '24px', fontWeight: 400 }}>
          {t('activation:step4.shareOf', { share: shareholder.percentageShare, companyName: ownerName })}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: COLORS.gray[1000], lineHeight: '21px' }}>
          {isJuridical(shareholder)
            ? t('activation:step4.juridicalRowHint')
            : t('activation:step4.physicalRowHint')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {renderStatusChip(shareholder)}
        <IconButton onClick={() => openEditShareholder(shareholder)}>
          <EditIcon />
        </IconButton>
        <IconButton onClick={() => setShareholderToDelete(shareholder)}>
          <TrashIcon size="sm" />
        </IconButton>
      </Box>
    </Box>
  );

  const renderShareTotal = (block: APIStakeholder[], ownerName: string) => {
    const total = block.reduce((sum, { percentageShare }) => sum + (percentageShare ?? 0), 0);
    const isComplete = Math.abs(total - FULL_SHARE) <= SHARE_TOTAL_TOLERANCE;
    return (
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: '21px',
          paddingTop: '12px',
          borderTop: `1px solid ${COLORS.gray[300]}`,
          color: isComplete ? COLORS.feedback.success : COLORS.gray[1000],
        }}
      >
        {isComplete
          ? t('activation:step4.shareTotalComplete', { companyName: ownerName })
          : t('activation:step4.shareTotalPartial', { companyName: ownerName, total })}
      </Typography>
    );
  };

  // A juridical shareholder gets its own block, and so do its juridical
  // shareholders, until the chain ends in physical persons.
  const renderJuridicalBlocks = (block: APIStakeholder[], ownerName: string): JSX.Element[] =>
    block.filter(isJuridical).flatMap((juridicalShareholder) => {
      const children = blocksByParentId.get(juridicalShareholder.id) ?? [];
      const isCollapsed = collapsedBlocks.includes(juridicalShareholder.id);
      return [
        <Box key={juridicalShareholder.id}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '8px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <IconButton
                onClick={() =>
                  setCollapsedBlocks((prev) =>
                    isCollapsed
                      ? prev.filter((id) => id !== juridicalShareholder.id)
                      : [...prev, juridicalShareholder.id]
                  )
                }
              >
                {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: COLORS.gray[1400],
                    lineHeight: '27px',
                  }}
                >
                  {t('activation:step4.shareholdersOf', { companyName: juridicalShareholder.fullName })}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: COLORS.gray[1000], lineHeight: '21px' }}>
                  {isCollapsed
                    ? t('activation:step4.shareholdersDeclared', { count: children.length })
                    : t('activation:step4.shareOf', {
                        share: juridicalShareholder.percentageShare,
                        companyName: ownerName,
                      })}
                </Typography>
              </Box>
            </Box>
            <Button
              type="outline"
              color="secondary"
              size="small"
              onClick={() => openAddShareholder(juridicalShareholder)}
              startIcon={<AddIcon />}
            >
              {t('activation:step4.add')}
            </Button>
          </Box>
          <Collapse in={!isCollapsed}>
            {children.length === 0 ? (
              <Box sx={{ padding: '16px 0', borderTop: `1px solid ${COLORS.gray[300]}` }}>
                <Typography sx={{ fontSize: '14px', color: COLORS.gray[1000] }}>
                  {t('activation:step4.noNestedShareholders')}
                </Typography>
              </Box>
            ) : (
              <>
                {children.map((child) => renderShareholderRow(child, juridicalShareholder.fullName))}
                {renderShareTotal(children, juridicalShareholder.fullName)}
              </>
            )}
          </Collapse>
        </Box>,
        ...renderJuridicalBlocks(children, juridicalShareholder.fullName),
      ];
    });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {showShareholderDrawer && (
        <AddEditStakeholderDrawer
          stakeholder={currentShareholder}
          companyId={companyId}
          parentName={currentParent?.fullName ?? companyName}
          onAddStakeholder={async (targetCompanyId, values, files) => {
            await addCompanyShareholder(targetCompanyId, values, files, false, currentParent?.id);
            closeShareholderDrawer();
          }}
          onEditStakeholder={async (targetCompanyId, stakeholderId, values) => {
            await editCompanyShareholder(targetCompanyId, stakeholderId, values, false);
            closeShareholderDrawer();
          }}
          onUpdateStakeholderAsset={async (targetCompanyId, stakeholderId, assetId, values) => {
            await updateShareholderAsset(targetCompanyId, stakeholderId, assetId, values, false);
          }}
          handleClose={closeShareholderDrawer}
          showDrawer={showShareholderDrawer}
          isShareholder
          allowAddControllingPerson={effectiveControllingPersons.length === 0}
        />
      )}
      {showControllingPersonDrawer && (
        <AddEditStakeholderDrawer
          stakeholder={currentControllingPerson}
          companyId={companyId}
          onAddStakeholder={async (targetCompanyId, values, files) => {
            if (values.isControllingPerson) {
              // User marked "also a shareholder": save as shareholder with is_controlling_person=true.
              await addCompanyShareholder(targetCompanyId, values, files, false);
            } else {
              await addCompanyLegalRepresentative(targetCompanyId, values, files, false);
            }
            closeControllingPersonDrawer();
          }}
          onEditStakeholder={async (targetCompanyId, stakeholderId, values) => {
            await editCompanyLegalRepresentative(targetCompanyId, stakeholderId, values, false);
            closeControllingPersonDrawer();
          }}
          onUpdateStakeholderAsset={async (targetCompanyId, stakeholderId, assetId, values) => {
            await updateLegalRepresentativeAsset(targetCompanyId, stakeholderId, assetId, values, false);
          }}
          handleClose={closeControllingPersonDrawer}
          showDrawer={showControllingPersonDrawer}
          stakeholderCopyKey="controllingPersons"
        />
      )}

      <Modal isOpen={!!shareholderToDelete} handleClose={() => setShareholderToDelete(null)} size="small">
        <Grid container rowSpacing={2}>
          <Grid item xs={12}>
            <Typography>
              {t('client:documents.shareholders.confirmDelete', {
                name: shareholderToDelete?.fullName,
              })}
            </Typography>
            {!!shareholderToDelete && countDescendants(shareholderToDelete) > 0 && (
              <Typography sx={{ marginTop: '8px', color: COLORS.feedback.danger }}>
                {t('activation:step4.confirmDeleteChain', {
                  count: countDescendants(shareholderToDelete),
                })}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: '4px' }}>
              <Button color="danger" isLoading={isDeleting} onClick={handleDeleteShareholder}>
                {t('client:documents.shareholders.confirmDeleteBtn')}
              </Button>
              <Button type="outline" color="danger" onClick={() => setShareholderToDelete(null)}>
                {t('client:documents.shareholders.confirmDeleteCancel')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Modal>

      <Grid container rowSpacing={3}>
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '36px', color: COLORS.brand.night }}
          >
            {t('activation:step4.title')}
          </Typography>
          <Typography
            sx={{ fontSize: '18px', fontWeight: 400, lineHeight: '27px', color: COLORS.gray[1100] }}
          >
            {t('activation:step4.subtitle')}
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ marginTop: '8px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              borderRadius: '12px',
              backgroundColor: COLORS.brand.white,
              border: `1px solid ${COLORS.gray[200]}`,
              padding: '24px',
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <Typography
                  sx={{ fontSize: '18px', fontWeight: 600, color: COLORS.gray[1400], lineHeight: '27px' }}
                >
                  {t('activation:step4.shareholders')}
                </Typography>
                <Button
                  type="outline"
                  color="secondary"
                  size="small"
                  onClick={() => openAddShareholder(null)}
                  startIcon={<AddIcon />}
                >
                  {t('activation:step4.add')}
                </Button>
              </Box>
              {isLoadingShareholders ? (
                <Skeleton height={64} />
              ) : rootShareholders.length === 0 ? (
                <Box
                  sx={{
                    padding: '16px 0',
                    borderTop: `1px solid ${COLORS.gray[300]}`,
                  }}
                >
                  <Typography sx={{ fontSize: '14px', color: COLORS.gray[1000] }}>
                    {t('activation:step4.noShareholders')}
                  </Typography>
                </Box>
              ) : (
                <>
                  {rootShareholders.map((shareholder) => renderShareholderRow(shareholder, companyName))}
                  {renderShareTotal(rootShareholders, companyName)}
                </>
              )}
            </Box>

            {!isLoadingShareholders && renderJuridicalBlocks(rootShareholders, companyName)}

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <Typography
                  sx={{ fontSize: '18px', fontWeight: 600, color: COLORS.gray[1400], lineHeight: '27px' }}
                >
                  {t('activation:step4.controllingPersons')}
                </Typography>
                <Tooltip
                  title={
                    effectiveControllingPersons.length > 0
                      ? t('activation:step4.addControllingPersonDisabledTooltip')
                      : ''
                  }
                >
                  <span>
                    <Button
                      type="outline"
                      color="secondary"
                      size="small"
                      disabled={effectiveControllingPersons.length > 0}
                      onClick={() => {
                        setCurrentControllingPerson(null);
                        setShowControllingPersonDrawer(true);
                      }}
                      startIcon={<AddIcon />}
                    >
                      {t('activation:step4.add')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              {isLoadingControllingPersons || isLoadingShareholders ? (
                <Skeleton height={64} />
              ) : effectiveControllingPersons.length === 0 ? (
                <Box
                  sx={{
                    padding: '16px 0',
                    borderTop: `1px solid ${COLORS.gray[300]}`,
                  }}
                >
                  <Typography sx={{ fontSize: '14px', color: COLORS.gray[1000] }}>
                    {t('activation:step4.noControllingPersons')}
                  </Typography>
                </Box>
              ) : (
                effectiveControllingPersons.map((controllingPerson) => (
                  <Box
                    key={controllingPerson.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '12px 0',
                      borderTop: `1px solid ${COLORS.gray[300]}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: COLORS.gray[1200],
                          lineHeight: '24px',
                        }}
                      >
                        {controllingPerson.fullName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          color: COLORS.gray[1100],
                          lineHeight: '24px',
                          fontWeight: 400,
                        }}
                      >
                        {controllingPerson.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          lineHeight: '21px',
                          color: COLORS.feedback.success,
                          backgroundColor: COLORS.feedback.successBackground,
                          borderRadius: '10px',
                          padding: '4px 10px',
                        }}
                      >
                        {t('activation:step4.complete')}
                      </Box>
                      <IconButton onClick={() => handleEditControllingPerson(controllingPerson)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      <StepButtons
        onNext={() =>
          onNext({
            shareholderCount: shareholders.length,
            controllingPersonCount: effectiveControllingPersons.length,
          })
        }
        onBack={onBack}
        isNextLoading={isNextLoading}
        isNextDisabled={isNextDisabled}
        nextLabel={t('activation:buttons.finish')}
      />
    </Box>
  );
};

export default Ownership;
